import dynamic from 'next/dynamic';
import React from 'react';

import { useTrackViewedElement } from '@/domains/core/tracking/hooks/useTrackViewedElement';
import { useFetchProductComparator } from '@/domains/productDiscovery/ProductPage/common/modules/ProductComparator/hooks/useFetchProductComparator';
import { useIsProductComparatorEnabled } from '@/domains/productDiscovery/ProductPage/common/modules/ProductComparator/hooks/useIsProductComparatorEnabled';
import { ProductComparatorSkeleton } from '@/productDiscovery/ProductPage/common/modules/ProductComparator/ProductComparatorSkeleton';

const ProductComparator = dynamic(
  () =>
    import(
      '@/domains/productDiscovery/ProductPage/common/modules/ProductComparator/ProductComparator'
    ).then((module) => module.ProductComparator),
  {
    ssr: false,
    loading: () => <ProductComparatorSkeleton />,
  },
);

type ProductComparatorContainerProps = {
  className?: string;
};

export const ProductComparatorContainer = ({
  className,
}: ProductComparatorContainerProps) => {
  const { isEnabled } = useIsProductComparatorEnabled();
  const { fetching, productsToDisplay, fetch } = useFetchProductComparator();

  const [productComparatorRef] = useTrackViewedElement(fetch, {
    resetViewedWithSearchParams: true,
    delay: 100,
    rootMargin: '200px',
    threshold: 0,
  });

  if (!isEnabled) return null;

  if (fetching) {
    return (
      <div ref={productComparatorRef}>
        <ProductComparatorSkeleton />
      </div>
    );
  }

  if (!productsToDisplay.length) return null;

  return (
    <ProductComparator
      fetching={fetching}
      productsToDisplay={productsToDisplay}
    />
  );
};

ProductComparatorContainer.displayName = 'ProductComparator.container';
